import {
  executeGraphqlRequest,
  readCachedGraphqlQuery,
} from '../apollo/graphql-request';

export type ProposalStatus =
  | 'APPROVED'
  | 'CANCELED'
  | 'CONFIRMED'
  | 'DECLINED'
  | 'DISCUSSING'
  | 'REJECTED'
  | 'SUBMITTED'
  | 'VIEWED';

export interface ProposalSummary {
  uuid: string;
  positionUuid: string;
  profileUuid: string;
  serviceUuid?: string | null;
  conversationUuid?: string | null;
  price: number;
  currency: string;
  message?: string | null;
  status: ProposalStatus;
  createdAt: string;
  updatedAt: string;
  position: {
    uuid: string;
    title: string;
  };
  service?: {
    uuid: string;
    title: string;
  } | null;
}

const PROPOSAL_SELECTION_SET = `
  uuid
  positionUuid
  profileUuid
  serviceUuid
  price
  currency
  message
  status
  createdAt
  updatedAt
  position {
    uuid
    title
  }
  service {
    uuid
    title
  }
`;

const PROFILE_PROPOSALS_QUERY = `
  query ProfilePositionOffers($profileUuid: ID!) {
    profilePositionOffers(profileUuid: $profileUuid) {
      ${PROPOSAL_SELECTION_SET}
    }
  }
`;

const CONFIRM_PROPOSAL_MUTATION = `
  mutation AcceptPositionOffer($input: AcceptPositionOfferInput!) {
    acceptPositionOffer(input: $input) {
      uuid
      status
    }
  }
`;

const DECLINE_PROPOSAL_MUTATION = `
  mutation RejectPositionOffer($input: RejectPositionOfferInput!) {
    rejectPositionOffer(input: $input) {
      uuid
      status
    }
  }
`;

async function executeProposalRequest<TData>(
  query: string,
  variables: Record<string, unknown>,
  accessToken?: string
): Promise<TData> {
  return executeGraphqlRequest<TData>(query, variables, {
    accessToken,
    requestName: 'Proposal',
  });
}

export async function fetchProfileProposals(
  profileUuid: string,
  archived = false,
  accessToken?: string
): Promise<ProposalSummary[]> {
  const response = await executeProposalRequest<{
    profilePositionOffers: ProposalSummary[];
  }>(
    PROFILE_PROPOSALS_QUERY,
    {
      profileUuid,
    },
    accessToken
  );

  return response.profilePositionOffers ?? [];
}

export function readCachedProfileProposals(
  profileUuid: string,
  archived = false
): ProposalSummary[] | undefined {
  const cachedData = readCachedGraphqlQuery<{
    profilePositionOffers: ProposalSummary[];
  }>(PROFILE_PROPOSALS_QUERY, {
    profileUuid,
  });

  return cachedData?.profilePositionOffers ?? (cachedData ? [] : undefined);
}

export async function confirmProposalRequest(
  offerUuid: string,
  accountUuid: string,
  accessToken?: string
): Promise<void> {
  await executeProposalRequest(
    CONFIRM_PROPOSAL_MUTATION,
    {
      input: {
        accountUuid,
        offerUuid,
      },
    },
    accessToken
  );
}

export async function declineProposalRequest(
  offerUuid: string,
  accountUuid: string,
  accessToken?: string
): Promise<void> {
  await executeProposalRequest(
    DECLINE_PROPOSAL_MUTATION,
    {
      input: {
        accountUuid,
        offerUuid,
      },
    },
    accessToken
  );
}
